import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild } from "@angular/core";
import { AiEditorActionsService, IBaseChatEditorActionParameterType, IChatEditorAction, IChatEditorActionParamDetails } from "@common/lib/chat-ai/ai-editor-actions.service";
import { QueuedCaller } from "@common/lib/queued-caller/queued-caller";
import { ElementUtilities } from "@common/lib/utilities/element-utilities";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { BaseDialogComponent } from "@common/ux/adapt-common-dialog/base-dialog.component/base-dialog.component";
import { SimpleValueType } from "@common/ux/edit-simple-value-breeze-entity-dialog/edit-simple-value-breeze-entity-dialog.component";
import { AdaptHtmlEditorComponent } from "@common/ux/html-editor/html-editor.component";
import LoadIndicator, { Properties } from "devextreme/ui/load_indicator";
import { ValueChangedEvent } from "devextreme/ui/select_box";
import rehypeParse from "rehype-parse";
import rehypeRemark from "rehype-remark";
import remarkGfm from "remark-gfm";
import remarkStringify from "remark-stringify";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { unified } from "unified";
import { MarkdownParser } from "../chat-ai.service";


export enum EditorEditAction {
    Replace,
    InsertBefore,
    InsertAfter,
}

export interface IAiActionDialogResult {
    result: string;
    action: EditorEditAction;
}

export interface IAiActionDialogData<T extends IBaseChatEditorActionParameterType> {
    action: IChatEditorAction<T>;
    text: string;
    params?: IBaseChatEditorActionParameterType;
    customActions?: IChatEditorAction[];
}

@Component({
    selector: "adapt-ai-action-dialog",
    standalone: false,
    templateUrl: "./ai-action-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./ai-action-dialog.component.scss",
})
export class AiActionDialogComponent<T extends IBaseChatEditorActionParameterType> extends BaseDialogComponent<IAiActionDialogData<T>, IAiActionDialogResult> implements OnInit {
    public readonly dialogName = "aiAction";

    public previousResultText = "";
    public resultText = "";
    public rawResultText = "";

    public isLoading = false;
    public copiedText = false;

    public collapseOriginalText = true;

    @ViewChild(AdaptHtmlEditorComponent) public set adaptHtmlEditorComponent(value: AdaptHtmlEditorComponent | undefined) {
        if (value) {
            value.setElementMaxHeightToViewportBottom(10);
            this.queuedEditor.setCallee(value);
        }
    }

    private loadIndicator?: LoadIndicator;
    private queuedEditor = new QueuedCaller<AdaptHtmlEditorComponent>();

    public params: IBaseChatEditorActionParameterType = {};
    public actions: Pick<IChatEditorAction, "type" | "displayText">[] = [];
    public selectedAction: IChatEditorAction;

    private interruptLoadSubject$ = new Subject<void>();
    private markdownText: string;

    constructor(
        @Inject(ADAPT_DIALOG_DATA) public data: IAiActionDialogData<T>,
        private aiEditorActionsService: AiEditorActionsService,
    ) {
        super();

        // convert to markdown for prompt processing - this allows the prompts to use markdown formatting (like line breaks) while still showing the original formatting in the editor.
        this.markdownText = this.toMarkdown(data.text);
        this.selectedAction = data.action;
        this.params = data.params ?? {};
        this.actions = Object.values(this.aiEditorActionsService.getEditorActions())
            .concat(this.data.customActions ?? [])
            .map((action) => ({ type: action.type, displayText: action.displayText }));
    }

    public get hasParameters() {
        return !!this.selectedAction.paramDetails;
    }

    public get commandParams() {
        if (!this.hasParameters) {
            return [];
        }

        return Object.entries(this.selectedAction.paramDetails ?? {});
    }

    public get parametersAreSet() {
        return !this.hasParameters
            || this.commandParams.every(([param]) => Object.prototype.hasOwnProperty.call(this.params, param));
    }

    public get hasTextArea() {
        return this.hasParameters
            && this.commandParams.some(([_, paramDetails]) => this.isTextAreaEdit(paramDetails));
    }

    public isSelectEdit(paramDetail: IChatEditorActionParamDetails) {
        return paramDetail.type === SimpleValueType.Select;
    }

    public isTextAreaEdit(paramDetail: IChatEditorActionParamDetails) {
        return paramDetail.type === SimpleValueType.TextArea;
    }

    public ngOnInit() {
        // don't autoload when there's a text area
        if (this.parametersAreSet && !this.hasTextArea) {
            setTimeout(() => this.load());
        }
    }

    public interruptLoad() {
        this.interruptLoadSubject$.next();
        this.resultText = this.previousResultText;
    }

    public load() {
        if (this.markdownText) {
            this.isLoading = true;
            this.previousResultText = this.resultText;
            this.resultText = "";
            this.rawResultText = "";
            this.refreshLoadIndicator();
            this.getResult().pipe(
                takeUntil(this.interruptLoadSubject$),
                this.takeUntilDestroyed(),
                finalize(() => {
                    this.isLoading = false;
                    this.refreshLoadIndicator();
                }),
            ).subscribe((result) => {
                this.rawResultText = result;
                // parse as markdown so that it shows with linebreaks in the preview
                this.resultText = MarkdownParser.processSync(result).toString();
            });
        }
    }

    public replace() {
        this.resolve({ action: EditorEditAction.Replace, result: this.resultText });
    }

    public onActionChange(event: ValueChangedEvent) {
        if (event.event) {
            const action = Object.values(this.aiEditorActionsService.getEditorActions())
                .concat(this.data.customActions ?? [])
                .find((a) => a.type === event.value);
            if (action) {
                this.selectedAction = action;
                this.resultText = "";
                this.rawResultText = "";

                // immediately generate if the params are set
                if (this.parametersAreSet && !this.hasTextArea) {
                    this.load();
                }
            }
        }
    }

    public onParamChange(event: ValueChangedEvent) {
        if (event.event && this.parametersAreSet && !this.hasTextArea) {
            this.load();
        }
    }

    public async copyToClipboard(text: string) {
        await navigator.clipboard.writeText(text);
        this.copiedText = true;
        // reset back to uncopied so the button looks like it can be pressed again.
        setTimeout(() => this.copiedText = false, 5000);
    }

    private getResult() {
        return this.aiEditorActionsService.executeEditorAction(this.selectedAction, { text: this.markdownText, ...this.params });
    }

    private refreshLoadIndicator() {
        if (this.isLoading) {
            this.renderLoadIndicator();
        } else {
            this.disposeLoadIndicator();
        }
    }

    private renderLoadIndicator(): void {
        if (this.loadIndicator) {
            return;
        }

        this.queuedEditor.call((editor) => {
            ElementUtilities.waitUntilDescendantVisible(editor.elementRef.nativeElement, ".fr-element").pipe(
                this.takeUntilDestroyed(),
            ).subscribe((frElement) => {
                const $indicatorElement = $("<div>")
                    .addClass("dx-pending-indicator")
                    .appendTo(frElement);

                this.loadIndicator = new LoadIndicator($indicatorElement[0], {
                    // type is not exported in 25.1 but is supported still...
                    _animationType: "sparkle",
                    width: 48,
                    height: 48,
                } as unknown as Properties);
            });
        });
    }

    private disposeLoadIndicator() {
        if (!this.loadIndicator) {
            return;
        }

        this.loadIndicator.dispose();
        this.loadIndicator.$element().remove();
        this.loadIndicator = undefined;
    }

    private toMarkdown = (htmlContent: string) => unified()
        .use(rehypeParse)
        .use(rehypeRemark)
        .use(remarkGfm)
        .use(remarkStringify)
        .processSync(htmlContent)
        .toString()
        .trim();
}
